import React, { useEffect, useState, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, User, HandCoins, BarChart2 } from "lucide-react";

const EmployeeSidebar = memo(function EmployeeSidebar({ setCurrentPage }) {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    // Load existing data first
    const storedData = JSON.parse(localStorage.getItem("companyData"));
    setCompanyData(storedData);
    console.log("[EmployeeSidebar] Loaded from localStorage:", storedData);

    // Fetch latest company data from API
    if (storedData?.id) {
      import("../services/api").then(({ companyService }) => {
        companyService
          .getCompanyData(storedData.id)
          .then((res) => {
            if (res.success && res.company) {
              console.log(
                "[EmployeeSidebar] Updated company data from API:",
                res.company
              );
              setCompanyData(res.company);
              localStorage.setItem("companyData", JSON.stringify(res.company));
            }
          })
          .catch((err) => {
            console.error(
              "[EmployeeSidebar] Failed to refresh company data:",
              err
            );
          });
      });
    }

    // 🆕 Listen for company data updates (from SuperAdmin)
    const handleCompanyUpdate = (e) => {
      console.log("[EmployeeSidebar] 🔄 Company updated via event:", e.detail);
      setCompanyData(e.detail);
    };

    window.addEventListener("companyDataUpdated", handleCompanyUpdate);
    return () =>
      window.removeEventListener("companyDataUpdated", handleCompanyUpdate);
  }, []);

  const handleLogoClick = () => {
    navigate("/employee/profile");
    setCurrentPage("Profile");
  };

  return (
    <aside className="w-44 h-full bg-[#F5F5F5] shadow-lg border-r border-gray-200 flex-shrink-0">
      <div className="flex flex-col h-full">
        {/* Navigation Items */}
        <nav className="flex-1 px-4 py-4">
          <div className="space-y-1">
            {/* Always visible */}
            <Link
              to="/employee/profile"
              onClick={() => setCurrentPage("Profile")}
              className="flex items-center space-x-4 px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
            >
              <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                <User className="w-5 h-5" />
              </span>
              <span className="font-medium text-sm">Profile</span>
            </Link>

            {/* ✅ Attendance - only if enabled */}
            {companyData?.module_attendance === 1 && (
              <Link
                to="/employee/attendance"
                onClick={() => setCurrentPage("Attendance")}
                className="flex items-center space-x-4 px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
              >
                <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <Clock className="w-5 h-5" />
                </span>
                <span className="font-medium text-sm">Attendance</span>
              </Link>
            )}

            {/* ✅ Payroll - only if enabled */}
            {companyData?.module_payroll === 1 && (
              <Link
                to="/employee/payroll"
                onClick={() => setCurrentPage("Payroll")}
                className="flex items-center space-x-4 px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
              >
                <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <HandCoins className="w-5 h-5" />
                </span>
                <span className="font-medium text-sm">Payroll</span>
              </Link>
            )}

            {/* ✅ Reports - only if enabled */}
            {companyData?.module_reports === 1 && (
              <Link
                to="/employee/reports"
                onClick={() => setCurrentPage("Reports")}
                className="flex items-center space-x-4 px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
              >
                <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <BarChart2 className="w-5 h-5" />
                </span>
                <span className="font-medium text-sm">Reports</span>
              </Link>
            )}
          </div>
        </nav>
      </div>
    </aside>
  );
});

export default EmployeeSidebar;
